import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Permission } from "@alm/policy";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { RoleEditor } from "@/routes/_authenticated/_administration/settings/roles/-components/role-editor";

export const Route = createFileRoute("/_authenticated/platform_/roles/new")({
  beforeLoad: ({ context }) => guardRoute(context.me, "platform-console"),
  staticData: { fullWidth: true },
  component: NewNativeRolePage,
});

/** A native role from nothing: every organization sees it the moment it is saved. */
function NewNativeRolePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const grantable = useQuery(orpc.administration.roles.native.permissions.queryOptions({}));
  const create = useMutation(
    orpc.administration.roles.native.create.mutationOptions({
      onSuccess: async (role) => {
        toast.success(`${role.name} created for every organization`);
        await queryClient.invalidateQueries({ queryKey: orpc.administration.roles.key() });
        await navigate({ to: "/platform/roles/$roleId", params: { roleId: role.id } });
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  if (grantable.isPending) return <Skeleton className="h-96 w-full" />;
  if (grantable.isError) {
    return (
      <ReadFailed
        message="The permissions could not be loaded."
        error={grantable.error}
        retrying={grantable.isFetching}
        onRetry={() => void grantable.refetch()}
      />
    );
  }

  return (
    <RoleEditor
      title="New native role"
      initial={{ name: "", description: "", permissions: [] }}
      grantable={grantable.data as Permission[]}
      saveUnchanged
      place="console"
      pending={create.isPending}
      onSave={(values) => create.mutate(values)}
    />
  );
}
